import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowLeft } from 'lucide-react'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'
import { Select } from '../components/ui/select'
import { Textarea } from '../components/ui/textarea'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card'
import { useCreateIncomingInvoice } from '../hooks/useIncomingInvoices'
import { IncomingInvoiceCreate as IncomingInvoiceCreateType } from '../types/incomingInvoice'
import { toast } from '../hooks/useToast'
import { getApiError } from '../lib/api'

export default function IncomingInvoiceCreate() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const createMutation = useCreateIncomingInvoice()

  const [invoiceNumber, setInvoiceNumber] = useState('')
  const [date, setDate] = useState(new Date().toISOString().split('T')[0])
  const [receivedDate, setReceivedDate] = useState('')
  const [supplierName, setSupplierName] = useState('')
  const [supplierTaxId, setSupplierTaxId] = useState('')
  const [totalAmount, setTotalAmount] = useState<number | ''>('')
  const [currency, setCurrency] = useState('EUR')
  const [notes, setNotes] = useState('')

  const isSubmitting = createMutation.isPending

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    if (!invoiceNumber.trim() || !supplierName.trim() || totalAmount === '' || totalAmount <= 0) {
      toast({
        variant: 'destructive',
        title: t('incomingInvoices.toast.validationError'),
        description: t('incomingInvoices.toast.validationErrorDesc'),
      })
      return
    }

    const data: IncomingInvoiceCreateType = {
      invoiceNumber: invoiceNumber.trim(),
      date,
      receivedDate: receivedDate || undefined,
      supplier: {
        name: supplierName.trim(),
        taxId: supplierTaxId.trim() || null,
      },
      totalAmount: Number(totalAmount),
      currency,
      notes: notes.trim() || undefined,
    }

    try {
      await createMutation.mutateAsync(data)
      toast({
        title: t('incomingInvoices.toast.created'),
        description: t('incomingInvoices.toast.createdDesc'),
      })
      navigate('/dashboard/incoming-invoices')
    } catch (err) {
      const apiError = getApiError(err)
      toast({
        variant: 'destructive',
        title: t('incomingInvoices.toast.errorCreating'),
        description: apiError.message,
      })
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate('/dashboard/incoming-invoices')}
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold tracking-tight">
            {t('incomingInvoices.create')}
          </h1>
          <p className="text-muted-foreground">
            {t('incomingInvoices.createSubtitle')}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <Card>
          <CardHeader>
            <CardTitle>{t('incomingInvoices.form.details')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="invoiceNumber">
                  {t('incomingInvoices.form.invoiceNumber')} *
                </Label>
                <Input
                  id="invoiceNumber"
                  value={invoiceNumber}
                  onChange={e => setInvoiceNumber(e.target.value)}
                  placeholder={t('incomingInvoices.form.placeholders.invoiceNumber')}
                  disabled={isSubmitting}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="date">
                  {t('incomingInvoices.form.date')} *
                </Label>
                <Input
                  id="date"
                  type="date"
                  value={date}
                  onChange={e => setDate(e.target.value)}
                  disabled={isSubmitting}
                  required
                />
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="receivedDate">
                  {t('incomingInvoices.form.receivedDate')}
                </Label>
                <Input
                  id="receivedDate"
                  type="date"
                  value={receivedDate}
                  onChange={e => setReceivedDate(e.target.value)}
                  disabled={isSubmitting}
                />
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="supplierName">
                  {t('incomingInvoices.form.supplierName')} *
                </Label>
                <Input
                  id="supplierName"
                  value={supplierName}
                  onChange={e => setSupplierName(e.target.value)}
                  placeholder={t('incomingInvoices.form.placeholders.supplierName')}
                  disabled={isSubmitting}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="supplierTaxId">
                  {t('incomingInvoices.form.supplierTaxId')}
                </Label>
                <Input
                  id="supplierTaxId"
                  value={supplierTaxId}
                  onChange={e => setSupplierTaxId(e.target.value)}
                  placeholder={t('incomingInvoices.form.placeholders.supplierTaxId')}
                  disabled={isSubmitting}
                />
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="totalAmount">
                  {t('incomingInvoices.form.totalAmount')} *
                </Label>
                <Input
                  id="totalAmount"
                  type="number"
                  min="0"
                  step="0.01"
                  value={totalAmount}
                  onChange={e => setTotalAmount(e.target.value === '' ? '' : parseFloat(e.target.value))}
                  placeholder="0.00"
                  disabled={isSubmitting}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="currency">
                  {t('incomingInvoices.form.currency')}
                </Label>
                <Select
                  id="currency"
                  value={currency}
                  onChange={e => setCurrency(e.target.value)}
                  disabled={isSubmitting}
                >
                  <option value="EUR">EUR</option>
                  <option value="USD">USD</option>
                  <option value="GBP">GBP</option>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">
                {t('incomingInvoices.form.notes')}
              </Label>
              <Textarea
                id="notes"
                value={notes}
                onChange={e => setNotes(e.target.value)}
                placeholder={t('incomingInvoices.form.placeholders.notes')}
                disabled={isSubmitting}
                rows={3}
              />
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end gap-3 mt-6">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/dashboard/incoming-invoices')}
            disabled={isSubmitting}
          >
            {t('common.cancel')}
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting
              ? t('incomingInvoices.form.saving')
              : t('incomingInvoices.form.save')}
          </Button>
        </div>
      </form>
    </div>
  )
}
